import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import LanguageSelector from './LanguageSelector';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import logo from '../assets/images/alitero.webp';

const { FiMenu, FiX } = FiIcons;

const Header = () => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const links = [
    { path: '/', label: t.nav.home },
    { path: '/services', label: t.nav.services },
    { path: '/about', label: t.nav.about },
    { path: '/request', label: t.nav.request },
    { path: '/current', label: t.nav.current },
    { path: '/references', label: t.nav.references },
  ];

  const linkCls = (path) =>
    `text-sm font-medium transition-all duration-200 px-3 py-1.5 rounded-full outline-none ${
      isActive(path) ? 'text-amber-700 ring-1 ring-amber-700' : 'text-stone-400 hover:text-stone-600'
    }`;

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="Alitero Logo" className="h-[64px] w-auto" />
            <span className="font-serif font-bold text-2xl tracking-tight text-stone-900 hidden sm:inline">Alitero</span>
          </Link>

          {/* Desktop-Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {links.slice(0, 6).map((l) => (
              <Link key={l.path} to={l.path} className={linkCls(l.path)}>{l.label}</Link>
            ))}
            <LanguageSelector />
          </nav>

          {/* Mobile-Button */}
          <div className="md:hidden flex items-center space-x-4">
            <LanguageSelector />
            <button
              onClick={toggleMenu}
              style={{ color: '#a8a29e' }}
              className="hover:opacity-75 transition-opacity duration-200 relative w-6 h-6"
              aria-label={isMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
              aria-expanded={isMenuOpen}
            >
              <AnimatePresence initial={false} mode="wait">
                {isMenuOpen ? (
                  <motion.span key="close" initial={{ opacity: 0, rotate: -90 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 0, rotate: 90 }} transition={{ duration: 0.2 }} className="absolute inset-0 flex items-center justify-center">
                    <SafeIcon icon={FiX} className="h-6 w-6" />
                  </motion.span>
                ) : (
                  <motion.span key="open" initial={{ opacity: 0, rotate: 90 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 0, rotate: -90 }} transition={{ duration: 0.2 }} className="absolute inset-0 flex items-center justify-center">
                    <SafeIcon icon={FiMenu} className="h-6 w-6" />
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>

        {/* Mobile-Navigation */}
        <AnimatePresence initial={false}>
          {isMenuOpen && (
            <motion.div key="mobile-nav" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} className="md:hidden overflow-hidden border-t border-stone-200">
              <motion.div className="flex flex-col space-y-1 py-4" initial="hidden" animate="show" variants={{ hidden: { transition: { staggerChildren: 0.04 } }, show: { transition: { staggerChildren: 0.05, delayChildren: 0.05 } } }}>
                {links.map((link) => (
                  <motion.div key={link.path} variants={{ hidden: { opacity: 0, x: -16 }, show: { opacity: 1, x: 0 } }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}>
                    <Link to={link.path} onClick={() => setIsMenuOpen(false)} style={{ color: isActive(link.path) ? '#b45309' : '#a8a29e' }} className="block px-2 py-2 text-sm font-medium transition-colors duration-200">
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
